/**
 * Patient tracker (TIME) Redux domain models and actions.
 */
import { DropdownOption } from '@tcl-models/models';
import { MultiSelectDropdownProps } from '@tcl-boron-prefabs/multi-select-dropdown';
import {
  PatientCohortMatchWorkflowState,
  SnoozeData as StateflowSnoozeData,
  SnoozeDataType,
} from '@tempus/stateflow-types';
import type { Activity } from '~/types/tShared';
import {
  Institution,
  MatchIdentifiedBy,
  OperationalFlags,
  ProgramSite,
  Summary,
  TimePatientStatuses,
  TimeProgramSiteStatus,
  Trial,
  TrialArm,
} from '@tempus/trials-types';

import type { GenericIdNameObject } from '~/global';

import type { PatientDataWithMatchesDto } from './timePatientTracking';
import type { EligibilityReviewWorkflow, EvaluationEngineWorkflow, TimePatientVisit } from './timeScreeningStore';
import type { PATIENT_TRACKER_TAB } from '../../patientTrackerV3/constants';

export enum PatientTrackerStateId {
  ALL_PATIENTS_PAGE = 'allPatientsPage',
  MATERIALS_PAGE = 'materialsPage',
  MATCH_REVIEW_PAGE = 'matchReviewPage',
  TEMPUS_NURSE_PAGE = 'tempusNursePage',
}

export interface StoreKeys {
  stateMapKey: PatientTrackerStateId;
}

export interface PatientTrackerFilters {
  [PatientTrackerFilterField.BIOMARKER]: string;
  [PatientTrackerFilterField.INDICATIONS]: DropdownOption[];
  [PatientTrackerFilterField.MATCH_STATUS]: MultiSelectDropdownProps['value'];
  [PatientTrackerFilterField.NOTE]: string;
  [PatientTrackerFilterField.PATIENT_ID]: string;
  [PatientTrackerFilterField.PATIENT_NAME]: string;
  [PatientTrackerFilterField.PHYSICIAN]: string;
  [PatientTrackerFilterField.RN_ASSIGNMENT]: string;
  [PatientTrackerFilterField.SORT_ORDER]: DropdownOption;
  [PatientTrackerFilterField.TRIAL_NAME]: DropdownOption | null;
  [PatientTrackerFilterField.TRIAL_NAMES]: DropdownOption[];
  [PatientTrackerFilterField.TRIAL_STATUS]: DropdownOption[];
  [PatientTrackerFilterField.TRIAL_TYPE]: DropdownOption;
  [PatientTrackerFilterField.PROGRAMS]: DropdownOption[];
}

export enum PatientTrackerFilterField {
  BIOMARKER = 'biomarker',
  INDICATIONS = 'indications',
  MATCH_STATUS = 'matchStatus',
  NOTE = 'note',
  PATIENT_ID = 'patientId',
  PATIENT_NAME = 'patientName',
  PHYSICIAN = 'physician',
  RN_ASSIGNMENT = 'rnAssignment',
  SORT_ORDER = 'sortOrder',
  TRIAL_NAME = 'trialName',
  TRIAL_NAMES = 'trialNames',
  TRIAL_STATUS = 'trialStatus',
  TRIAL_TYPE = 'trialType',
  PROGRAMS = 'programs',
}

export const PatientTrackerSingleDropdownFilters = [
  PatientTrackerFilterField.SORT_ORDER,
  PatientTrackerFilterField.TRIAL_NAME,
  PatientTrackerFilterField.TRIAL_TYPE,
];

export const PatientTrackerMultiDropdownFilters = [
  PatientTrackerFilterField.INDICATIONS,
  PatientTrackerFilterField.TRIAL_NAMES,
  PatientTrackerFilterField.TRIAL_STATUS,
  PatientTrackerFilterField.MATCH_STATUS,
  PatientTrackerFilterField.PROGRAMS,
];

interface PatientTrackerState {
  categoryCounts: PatientTrackerTabCounts;
  exports: PatientRecordExportState;
  fetchingRecords: boolean;
  filters: PatientTrackerFilters;
  patients: PatientDataWithMatchesDto[];
  patientsMetadata: PatientRecordMetadata;
  tab: PATIENT_TRACKER_TAB;
}

export type PatientTrackerStateMap = Record<string, PatientTrackerState>;

interface PatientRecordExportState {
  data?: Blob;
  error?: unknown;
  fileName?: string;
  loading: boolean;
}

export interface PatientRecordMetadata {
  numRecords: number;
  skip: number;
  top?: number;
  totalRecords: number;
}

export interface PatientRecord {
  acknowledged: string;
  biomarker: string;
  cohortId: string;
  cohortName: string;
  dateCreated: string;
  institutionId: string;
  latestUpdates: string;
  latestUpdatesModifiedAt: string;
  matchDate: string;
  nctId: string;
  nextVisitDate: string | null;
  passedPharmaOpsReview: string;
  patientDob: string;
  patientFirstName: string;
  patientFullName: string;
  patientIdentifiedBy: string;
  patientLastName: string;
  patientMatchStatus: string;
  pharmaOpsReviewCompletedBy: string;
  pharmaOpsReviewCompletedDate: string;
  program: string;
  relatedPatientId: number;
  relatedTrialWorkflowId: number;
  tAppNotes: string;
  timePatientId: string;
  timeSite: string;
  trial: Pick<Trial, 'arms' | 'id' | 'nctId' | 'shortName'> & {
    programSites: ProgramSite[];
  };
  trialName: string;
  visitType: string;
}

export interface PatientTrackerTabCounts {
  [PATIENT_TRACKER_TAB.REVIEW_REQUIRED]?: number | null;
  [PATIENT_TRACKER_TAB.WATCHLIST]?: number | null;
  [PATIENT_TRACKER_TAB.CANDIDATE_NOW]?: number | null;
  [PATIENT_TRACKER_TAB.SCREENING_QUEUE]?: number | null;
  [PATIENT_TRACKER_TAB.PENDING_TEMPUS_REVIEW]?: number | null;
  [PATIENT_TRACKER_TAB.ENROLLED]?: number | null;
  [PATIENT_TRACKER_TAB.VISITING_THIS_WEEK]?: number | null;
  [PATIENT_TRACKER_TAB.TIME_SENSITIVE]?: number | null;
}

export interface PatientTrackingDetails {
  arm?: Pick<TrialArm, 'id' | 'name'> & { operationalFlag: OperationalFlags };
  armId: string;
  armSummary?: {
    summary: Summary;
    updatedAt: string;
  };
  biomarkers?: string | null;
  createdAt: string;
  currentState: PatientCohortMatchWorkflowState;
  dateConsentRecognizedByTempus: string | null;
  dateNotificationSentToSite: string | null;
  dateRnClearedForNotification: string | null;
  eligibilityReviewWorkflows?: EligibilityReviewWorkflow[];
  firstTreatmentDate?: string | null;
  identifiedBy: MatchIdentifiedBy;
  institutionId: string;
  internalRnToScreeningRnNote?: string | null;
  isPrescreeningBadgeDismissed: boolean | null;
  isRescreenBadgeDismissed: boolean | null;
  isReviewRequired?: boolean;
  mostRecentEvalEngineIsMatch: boolean | null;
  mostRecentEvaluationEngineWorkflow: EvaluationEngineWorkflow[];
  mrn?: string;
  newFlag?: boolean;
  noCredit: boolean;
  passedRnReview: string;
  patientCancerType: string;
  patientConsentedDate: string | null;
  patientId: string;
  patientLinkageId: string;
  preconsentDate?: string | null;
  preconsentRecognizedDate?: string | null;
  /** Site Trials vs TIME. Present on queue matches; site-trial prescreening rows set it when mapped. */
  programType?: string | number | null;
  programSite?: Pick<ProgramSite, 'pauseMatching' | 'programType' | 'status'>;
  reasonNotAMatch: string | null;
  reasonNotAMatchDetails: string | null;
  recruitmentVendor: string | null;
  rescreenFlag?: boolean;
  screenedBy?: string | null;
  siteArm: {
    programType?: string | number | null;
    status: TimeProgramSiteStatus;
  };
  snoozeData?: StateflowSnoozeData[];
  snoozeDate?: string;
  snoozeType?: SnoozeDataType;
  statusDate: string | null;
  tAppNotes?: string | null;
  tempusToPortalLatestPortalPatientUpdate: string | null;
  tempusToPortalLatestPortalUpdateModifiedDate: string;
  timePatientId: string;
  timeRnReviewCompletedDate?: string | null;
  trial: Pick<Trial, 'id' | 'nctId' | 'shortName' | 'title'> & {
    type: 'Observational' | 'Interventional';
  };
  updatedAt?: string;
  urgentFlag?: boolean;
  workflowId: string;
  workflowType?: string;
}

/** Snooze fields extracted from PCM {@link PatientTrackingDetails} for watchlist UI (not care-pathway {@link SnoozeData}). */
export type TrackerSnoozeFields = {
  date?: string;
  type?: SnoozeDataType;
};

export interface TimePatient {
  activities: Activity[];
  billingId: string;
  displayedRowCount?: number;
  id: string;
  institution: Institution;
  patient: Patient;
  patientLinkageId: string;
  patientTrackingDetails: (PatientTrackingDetails & { editing: boolean })[];
  physician?: Physician;
  physicianId: string | null;
  responseRequired: boolean;
  status: TimePatientStatuses;
  timePatientVisits: TimePatientVisit[];
}

export type Physician = GenericIdNameObject;

export interface PatientTrackingDetailsWithPatientMeta extends PatientTrackingDetails {
  dateOfBirth?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface Patient {
  dateOfBirth?: string | null;
  firstName?: string | null;
  fullName?: string;
  id: string;
  lastName?: string | null;
  linkageId: string;
  tempusId?: string;
  workflows: PatientTrackingDetails[];
}

export interface SyncedFields {
  consentDate: string | null;
  consentRecognizedDate: string | null;
  enrolledDate: string | null;
  internalRnToScreeningRnNote: string;
  preconsentDate: string | null;
}

export const GET_PATIENT_TRACKER_V3_RECORDS = 'GET_PATIENT_TRACKER_V3_RECORDS';
export const GET_PATIENT_TRACKER_V3_RECORDS_FAILED = 'GET_PATIENT_TRACKER_V3_RECORDS_FAILED';
export const SET_PATIENT_TRACKER_V3_RECORDS = 'SET_PATIENT_TRACKER_V3_RECORDS';
export const GET_PATIENT_TRACKER_V3_RECORDS_CSV = 'GET_PATIENT_TRACKER_V3_RECORDS_CSV';
export const GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED = 'GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED';
export const SET_PATIENT_TRACKER_V3_RECORDS_CSV = 'SET_PATIENT_TRACKER_V3_RECORDS_CSV';
export const CLEAR_PATIENT_TRACKER_V3_RECORDS_CSV = 'CLEAR_PATIENT_TRACKER_V3_RECORDS_CSV';
export const SET_PATIENT_TRACKER_TAB = 'SET_PATIENT_TRACKER_TAB';
export const GET_PATIENT_TRACKER_V3_CATEGORY_COUNTS = 'GET_PATIENT_TRACKER_V3_CATEGORY_COUNTS';
export const SET_PATIENT_TRACKER_V3_CATEGORY_COUNTS = 'SET_PATIENT_TRACKER_V3_CATEGORY_COUNTS';
export const SHOW_PATIENT_TRACKER_V3_ERROR_MESSAGE = 'SHOW_PATIENT_TRACKER_V3_ERROR_MESSAGE';
export const SET_PATIENT_TRACKER_V3_FILTERS = 'SET_PATIENT_TRACKER_V3_FILTERS';
export const SET_PATIENT_TRACKER_V3_PAGINATION = 'SET_PATIENT_TRACKER_V3_PAGINATION';

export interface GetPatientTrackerRecords {
  id: string;
  siteId: string;
  type: typeof GET_PATIENT_TRACKER_V3_RECORDS;
}

interface SetPatientTrackerRecords {
  id: string;
  patients: PatientDataWithMatchesDto[];
  type: typeof SET_PATIENT_TRACKER_V3_RECORDS;
}

interface GetPatientTrackerRecordsFailed {
  error: unknown;
  id: string;
  type: typeof GET_PATIENT_TRACKER_V3_RECORDS_FAILED;
}

interface SetPatientTrackerTab {
  id: string;
  tab: PATIENT_TRACKER_TAB;
  type: typeof SET_PATIENT_TRACKER_TAB;
}

export interface GetPatientCategoryCounts {
  id: string;
  siteId: string;
  type: typeof GET_PATIENT_TRACKER_V3_CATEGORY_COUNTS;
}

interface SetPatientCategoryCounts {
  categoryCounts: PatientTrackerTabCounts;
  id: string;
  type: typeof SET_PATIENT_TRACKER_V3_CATEGORY_COUNTS;
}

interface ShowPatientTrackerErrorMessage {
  defaultErrorMessage?: string;
  error?: unknown;
  id?: string;
  type: typeof SHOW_PATIENT_TRACKER_V3_ERROR_MESSAGE;
}

interface SetPatientTrackerFilters {
  filters: Partial<PatientTrackerFilters>;
  id: string;
  type: typeof SET_PATIENT_TRACKER_V3_FILTERS;
}

interface SetPatientTrackerPagination {
  id: string;
  numRecords: number;
  skip: number;
  totalRecords: number;
  type: typeof SET_PATIENT_TRACKER_V3_PAGINATION;
}

export interface GetPatientTrackerRecordsCsv {
  id: string;
  siteId: string;
  type: typeof GET_PATIENT_TRACKER_V3_RECORDS_CSV;
}

interface GetPatientTrackerRecordsCsvFailed {
  error: unknown;
  id: string;
  type: typeof GET_PATIENT_TRACKER_V3_RECORDS_CSV_FAILED;
}

interface SetPatientTrackerRecordsCsv {
  data: Blob;
  fileName: string;
  id: string;
  type: typeof SET_PATIENT_TRACKER_V3_RECORDS_CSV;
}

interface ClearPatientTrackerRecordsCsv {
  id: string;
  type: typeof CLEAR_PATIENT_TRACKER_V3_RECORDS_CSV;
}

export type PatientTrackerActionTypes =
  | ClearPatientTrackerRecordsCsv
  | GetPatientCategoryCounts
  | GetPatientTrackerRecords
  | GetPatientTrackerRecordsCsv
  | GetPatientTrackerRecordsCsvFailed
  | GetPatientTrackerRecordsFailed
  | SetPatientCategoryCounts
  | SetPatientTrackerFilters
  | SetPatientTrackerPagination
  | SetPatientTrackerRecords
  | SetPatientTrackerRecordsCsv
  | SetPatientTrackerTab
  | ShowPatientTrackerErrorMessage;
